import * as THREE from 'three'
import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry.js'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import type { Beat, Character, DioramaProp } from '../data/cutscenes'
import { CUTSCENE_FOOD_CONTACT, CUTSCENE_THROW_RELEASE } from '../data/cutscenes'
import { cloneModelTemplate, loadAnimatedModel } from '../utils/glb-loader'
import type { CollisionBody } from './CollisionGuard'

const clamp = (p: number) => Math.max(0, Math.min(1, p))
const smooth = (p: number) => { const t = clamp(p); return t * t * (3 - 2 * t) }
const STORY_SEABED_Y = 0.025
const chapter = (shot: string) => Number(/^t(\d+)-/.exec(shot)?.[1] ?? 0)
const hasLastBag = (shot: string) => shot === 'intro-3d'
  || /^t[456]-/.test(shot) || /^t7-(2a|2b|3-01)/.test(shot)
const hasChart = (shot: string) => shot === 't6-tilt-01' || /^t7-(2a|2b|3)/.test(shot)
  || (chapter(shot) >= 8 && chapter(shot) <= 10)
const hasChest = (shot: string) => /^intro-(2|3)/.test(shot) || chapter(shot) >= 9 || shot.startsWith('ending-')

export function storyShipBagPosition(progress: number): THREE.Vector3 {
  const p = smooth(progress)
  return new THREE.Vector3(3.8 - p * 2.7, 0.8 + Math.sin(p * Math.PI) * 0.2, -0.2 - p * 2.5)
}

export function storyBarrelPosition(): THREE.Vector3 { return new THREE.Vector3(0.2, 0, 0.9) }

export function storyPlateStackPosition(shot: string, progress: number, bob: THREE.Vector3): THREE.Vector3 {
  const held = bob.clone().add(new THREE.Vector3(0.48,1.35,0.65))
  const counter = new THREE.Vector3(-0.55,1.58,-1.8)
  if (shot === 'intro-2b') return held.lerp(counter,smooth(progress / 0.5))
  return ['intro-2c','intro-3a-01','intro-4e-02'].includes(shot) ? counter : held
}

export function storyTrayPosition(shot: string, progress: number, bob: THREE.Vector3): THREE.Vector3 {
  const counter = new THREE.Vector3(0.8,1.58,-1.8)
  return shot === 'intro-3a-01' ? counter.lerp(bob.clone().add(new THREE.Vector3(0.35,1.22,0.68)),smooth(progress / 0.48)) : counter
}

export function storyLoosePlatePosition(shot: string, progress: number, bob: THREE.Vector3): THREE.Vector3 {
  const held = bob.clone().add(new THREE.Vector3(0.75,1.65,0.6))
  if (shot === 'intro-2b') return new THREE.Vector3(1.7,1.61,-1.8).lerp(held,smooth((progress - 0.55) / 0.45))
  if (shot === 'intro-3a-01') return held.lerp(storyTrayPosition(shot,progress,bob).add(new THREE.Vector3(0,0.11,0)),smooth(progress / 0.48))
  return held
}

const COURIER_SHOTS = ['t2-2a-02','t3-1b-01','t4-1a-03','t6-1c-01']

/** Shared prop/extra mark: a handoff first, then one uninterrupted townbound exit. */
export function storyCourierPosition(shot: string, progress: number, bob: THREE.Vector3): THREE.Vector3 {
  const departure = shot === 't7-3-01' ? smooth(progress) : smooth((progress - 0.25) / 0.7)
  return bob.clone().add(new THREE.Vector3(-0.65, 0.6, 0.1)).lerp(new THREE.Vector3(-7, 1.1, -3), departure)
}

export function storyFeedingMouth(shot: string, progress: number, _elapsed: number, bob: THREE.Vector3): THREE.Vector3 {
  if (shot === 't2-2a-02') return storyCourierPosition(shot,progress,bob).add(new THREE.Vector3(0,.95,.44))
  if (shot === 't5-2-03') return storyBarrelPosition().add(new THREE.Vector3(-smooth(progress)*.25,.95+smooth(progress)*.6,.44))
  // Contacts are evaluated from the shot timeline. A wall-clock wobble here made
  // the food/guard handoff change position between dropped and normal frames.
  if (shot === 't4-2b-04') return new THREE.Vector3(-3.3,2.55+Math.sin(progress*Math.PI)*.13,-1.06)
  return new THREE.Vector3(0,1.85,-.56)
}
export function storyGiantRingCenter(bob: THREE.Vector3): THREE.Vector3 { return bob.clone().add(new THREE.Vector3(0,2.1,.65)) }
export function storyGiantGuardMouth(index: number, _progress: number, bob: THREE.Vector3): THREE.Vector3 {
  const angle=index/6*Math.PI*2
  return storyGiantRingCenter(bob).add(new THREE.Vector3(Math.cos(angle)*1.15,0,Math.sin(angle)*1.15))
}
const GIANT_FOOD_SHOTS=['t7-3-02','t7-3-04']
// Only shots whose screenplay explicitly calls for food render the sandwich.
// t2-2a-02 is the courier handoff and t4-2b-04 is the King's bag theft; both
// previously inherited this list and spawned an unexplained sandwich through a
// character during an unrelated beat.
const FOOD_SHOTS=['t2-1a-02','t2-1b-02','t2-1c-03','t5-2-03','t10-3-04']
const ANIMATED_PROP_URL = '/models/cutscene/story-props-animated.glb'
const ANIMATED_PROP_ROOTS: Partial<Record<DioramaProp, string>> = {
  'coin-chest': 'CutsceneProp_CoinChest',
  'coin-bag': 'CutsceneProp_CoinBag',
  patty: 'CutsceneProp_TreasurePatty',
  'jelly-drop': 'CutsceneProp_JellyDrop',
  bridge: 'CutsceneProp_BridgeBubbles',
  'ultimate-sandwich': 'CutsceneProp_UltimateSandwich',
  throne: 'CutsceneProp_RoyalThrone',
}
const ANIMATED_PROP_CLIPS: Partial<Record<DioramaProp, Partial<Record<string, string>>>> = {
  'coin-chest': { 'knock-over': 'ChestBurstOpen', roll: 'ChestBurstOpen', bounce: 'ChestBurstOpen', 'burst-open': 'ChestBurstOpen', count: 'ChestBurstOpen' },
  'coin-bag': { collect: 'BagCarry', steal: 'BagCarry', 'swarm-carry': 'BagCarry', catch: 'BagCarry', reveal: 'BagCarry', discover: 'BagCarry' },
  patty: { collect: 'PattyFloat', reveal: 'PattyFloat', watch: 'PattyFloat', throw: 'PattyFloat' },
  'jelly-drop': { discover: 'JellyPulse', reveal: 'JellyPulse', collect: 'JellyPulse' },
  bridge: { repair: 'BridgeRepair', 'pop-bubble': 'BridgeRepair', 'form-bubble': 'BridgeRepair', stabilize: 'BridgeRepair' },
  'ultimate-sandwich': { build: 'UltimateGlow', offer: 'UltimateGlow', throw: 'UltimateGlow', eat: 'UltimateGlow', reward: 'UltimateGlow' },
  throne: { 'collapse-throne': 'ThroneCollapse' },
}

/** The chest is a procedural continuity prop. The exported Blender chest is a
 * root-only review asset with a mismatched light material, so showing both made
 * a second pale disc cross the opening shot. Other props may still use their
 * authored Blender clip when a beat explicitly asks for it. */
export function storyPropsUseAnimatedClip(beat: Beat, prop: DioramaProp): boolean {
  if (prop === 'coin-chest') return false
  return Boolean(beat.actions.some((action) => action.prop === prop && ANIMATED_PROP_CLIPS[prop]?.[action.type]))
}

export interface StoryPropSnapshot {
  name: string
  visible: boolean
  purpose: string
  position: { x: number; y: number; z: number }
  rotation: { x: number; y: number; z: number }
  bounds: { minY: number; maxY: number; width: number; depth: number }
}

export function storySandwichPosition(shot: string, progress: number, elapsed: number, positionOf: (character: Character)=>THREE.Vector3, mouthOf?: (character: Character)=>THREE.Vector3): THREE.Vector3 {
  const bob=positionOf('bluebob'), zap=positionOf('zap'), king=positionOf('king')
  const held=bob.clone().add(new THREE.Vector3(.65,1.45,.75))
  if (GIANT_FOOD_SHOTS.includes(shot)) return storyGiantRingCenter(bob)
  if (shot === 't2-1a-02' || shot === 't2-1c-03') return held
  if (shot === 't2-1b-02') {
    if (progress < CUTSCENE_THROW_RELEASE) return held
    return held.lerp(mouthOf?.('zap') ?? zap.clone().add(new THREE.Vector3(0,1.15,.72)), smooth((progress - CUTSCENE_THROW_RELEASE) / (CUTSCENE_FOOD_CONTACT['t2-1b-02'] - CUTSCENE_THROW_RELEASE)))
  }
  if (shot === 'ending-1b') {
    const result=held.lerp(mouthOf?.('king') ?? king.clone().add(new THREE.Vector3(0,1.45,1.1)),smooth(progress/.55))
    result.y+=Math.sin(clamp(progress/.55)*Math.PI)*.55
    return result
  }
  const start=shot === 't2-2a-02' ? zap.clone().add(new THREE.Vector3(-.5,.9,.65)) : held
  if (shot === 't10-3-04') {
    if (progress < CUTSCENE_THROW_RELEASE) return start
    return start.lerp(mouthOf?.('king') ?? king.clone().add(new THREE.Vector3(0,1.45,1.1)), smooth((progress - CUTSCENE_THROW_RELEASE) / (CUTSCENE_FOOD_CONTACT['t10-3-04'] - CUTSCENE_THROW_RELEASE)))
  }
  return start.lerp(storyFeedingMouth(shot,progress,elapsed,bob),smooth(progress/(shot === 't2-2a-02' ? .24 : .55)))
}

/** Suppress a legacy rig only where this module supplies its actual replacement. */
export function storyPropsOwnLegacy(beat: Beat, prop: DioramaProp): boolean {
  return ((prop === 'sandwich' || prop === 'giant-sandwich') && (FOOD_SHOTS.includes(beat.shot) || GIANT_FOOD_SHOTS.includes(beat.shot)))
    || (prop === 'coin-chest' && hasChest(beat.shot))
    || (prop === 'coin-bag' && (hasLastBag(beat.shot) || COURIER_SHOTS.includes(beat.shot)))
    || (prop === 'map' && hasChart(beat.shot))

}

/** Persistent, fully three-dimensional continuity props. Every pose derives from the
 * current shot and progress; jumping backward never retains future inventory. */
export class DioramaStoryProps {
  readonly root = new THREE.Group()
  readonly ready: Promise<void>
  private beat: Beat | null = null
  private groups = new Map<string, THREE.Group>()
  private materials = new Map<string, THREE.MeshStandardMaterial>()
  private geometries = new Set<THREE.BufferGeometry>()
  private textures = new Set<THREE.Texture>()
  private chestLid = new THREE.Group()
  private barrelLid = new THREE.Group()
  private disposed = false
  private animatedScene: THREE.Group | null = null
  private animatedMixer: THREE.AnimationMixer | null = null
  private animatedClips = new Map<string, THREE.AnimationClip>()
  private animatedRoots = new Map<DioramaProp, THREE.Object3D>()
  private animatedTargets = new Map<DioramaProp, THREE.Object3D>()
  private animatedBase = new Map<THREE.Object3D, { position: THREE.Vector3; rotation: THREE.Euler; scale: THREE.Vector3 }>()
  private activeAnimatedProp: DioramaProp | null = null

  constructor() {
    this.root.name = 'PhysicalStoryContinuity'
    this.satchel()
    this.chest()
    this.lastBag()
    this.plainBag('StoryRecoveredBag')
    for (let i = 0; i < 4; i++) this.plainBag(`StorySpilledBag${i}`)
    this.chart()
    this.plateStack('StoryPlates', 9)
    this.plateStack('StoryPlatesSecond', 4)
    this.plateStack('StorySurvivingPlate', 1)
    this.feastSign()
    this.servingTray()
    this.banquet()
    this.chair()
    this.crown()
    this.occupiedBarrel()
    this.parkedThrone()
    this.foodAndSupports()
    this.groups.forEach((group) => { group.visible = false })
    this.ready = loadAnimatedModel(ANIMATED_PROP_URL).then(({ scene, animations }) => {
      if (this.disposed) return
      this.animatedScene = cloneModelTemplate(scene)
      this.animatedScene.name = 'BlenderStoryPropAnimations'
      this.animatedScene.visible = false
      this.animatedScene.traverse((part) => {
        if (part instanceof THREE.Mesh) {
          part.castShadow = true
          part.receiveShadow = true
        }
      })
      for (const [prop, name] of Object.entries(ANIMATED_PROP_ROOTS) as [DioramaProp, string][]) {
        const object = this.animatedScene.getObjectByName(name)
        if (!object) continue
        // The Blender review scene is authored at mixed prop scales. Normalize each
        // root once so shot blocking can use the same stage units as the procedural kit.
        this.normaliseAnimatedRoot(object, prop)
        const holder = new THREE.Group()
        holder.name = `${name}_Holder`
        object.parent?.add(holder)
        holder.add(object)
        this.animatedRoots.set(prop, holder)
        this.animatedTargets.set(prop, object)
      }
      this.animatedClips.clear()
      for (const clip of animations) {
        this.animatedClips.set(clip.name.toLowerCase(), clip)
        this.animatedClips.set((clip.name.split('__').at(-1) ?? clip.name).toLowerCase(), clip)
      }
      this.animatedMixer = new THREE.AnimationMixer(this.animatedScene)
      this.root.add(this.animatedScene)
    }).catch(() => undefined)
  }

  get ownedProps(): ReadonlySet<DioramaProp> {
    const owned = new Set<DioramaProp>()
    if (!this.beat || this.disposed) return owned
    for (const prop of ['coin-chest', 'coin-bag', 'map', 'sandwich', 'giant-sandwich', 'sour-sandwich', 'ultimate-sandwich'] as const) {
      if (storyPropsOwnLegacy(this.beat, prop)) owned.add(prop)
    }
    if (this.animatedScene?.visible && this.activeAnimatedProp) owned.add(this.activeAnimatedProp)
    return owned
  }

  setBeat(beat: Beat): void {
    this.beat = beat
    this.animatedMixer?.stopAllAction()
    this.activeAnimatedProp = null
    if (this.animatedScene) this.animatedScene.visible = false
  }

  updateAttachments(matrixOf: (character: Character) => THREE.Matrix4 | undefined): void {
    const satchel = this.groups.get('StorySatchel')
    if (!satchel?.visible || this.disposed) return
    const matrix = matrixOf('bluebob')
    if (!matrix) return
    this.root.updateWorldMatrix(true, false)
    const local = this.root.matrixWorld.clone().invert().multiply(matrix)
    const position = satchel.position.clone()
    local.decompose(satchel.position, satchel.quaternion, satchel.scale)
    if (this.beat?.shot === 'intro-4d-02') satchel.position.copy(position)
    satchel.userData.collisionAttached = true
    satchel.updateMatrixWorld(true)
  }

  update(progress: number, elapsed: number, positionOf: (character: Character) => THREE.Vector3, mouthOf?: (character: Character) => THREE.Vector3, dt = 1 / 60, matrixOf?: (character: Character) => THREE.Matrix4 | undefined): void {
    if (!this.beat || this.disposed) return
    const beat = this.beat, shot = beat.shot, p = smooth(progress)
    const bob = positionOf('bluebob'), king = positionOf('king'), krabs = positionOf('krabs'), zap = positionOf('zap')
    this.groups.forEach((group) => {
      group.visible = false
      group.userData.collisionAttached = false
      group.position.set(0, 0, 0)
      group.rotation.set(0, 0, 0)
      group.scale.setScalar(1)
    })
    const show = (name: string, x: number, y: number, z: number) => {
      const group = this.groups.get(name)!
      group.visible = true
      group.position.set(x, y, z)
      return group
    }
    const attach = (name: string, actor: THREE.Vector3, x = 0, y = 0, z = 0) => show(name, actor.x + x, actor.y + y, actor.z + z)
    const quest = chapter(shot) > 0 || ['intro-4d-02','intro-4e-02','ending-1b','ending-2a','ending-2b-01','ending-2b-02','ending-3a'].includes(shot)
    if (quest && beat.cast.includes('bluebob')) {
      const satchel = attach('StorySatchel', bob)
      if (shot === 'intro-4d-02') satchel.position.lerpVectors(krabs, bob, p)
    }
    if (hasChest(shot)) {
      const chest = show('StoryFeastChest', 1, 0.1, -0.5)
      this.chestLid.rotation.x = 0
      if (shot.startsWith('intro-')) {
        chest.position.set(0.8, 1.54, -1.8)
        this.chestLid.rotation.x = /^intro-2/.test(shot) ? -0.7 : 0
        if (shot === 'intro-2c') {
          this.chestLid.rotation.x = -0.7 * (1 - smooth(progress / 0.35))
          chest.rotation.z = -0.3 * smooth((progress - 0.35) / 0.65)
        }
        if (shot === 'intro-3a-01') {
          chest.position.lerpVectors(new THREE.Vector3(0.8, 1.54, -1.8), new THREE.Vector3(1.5, 0.1, 1.2), p)
          chest.rotation.z = -p * Math.PI * 2
          this.chestLid.rotation.x = -0.75 * smooth((progress - 0.65) / 0.25)
        }
        if (shot === 'intro-3d') {
          // Start the burst on the rock-side lane. The previous start point
          // overlapped Krabs during the first third of the reveal before the
          // chest had visually cleared the counter.
          chest.position.lerpVectors(new THREE.Vector3(3.2, 0.1, 1.2), new THREE.Vector3(6, 3.4, -4), p)
          this.chestLid.rotation.x = -0.55 * (1 - smooth(progress / 0.3))
        }
      }
      if (chapter(shot) >= 9 || (shot.startsWith('ending-') && beat.scene === 'throne')) chest.position.set(-3.7, 0.1, -2.15)
      if (shot === 't9-2a-05') chest.position.z -= p * 2.5
      if (shot === 't10-1b-04') chest.position.x = 1.5 + p * 0.8
      if (shot === 'ending-2b-01') chest.position.lerpVectors(new THREE.Vector3(1, 0.1, -0.5), new THREE.Vector3(0, 0.1, 0.65), p)
      if (shot === 'ending-2b-02' || shot === 'ending-3a') {
        chest.position.copy(bob).lerp(king, 0.5).add(new THREE.Vector3(0, 0.7, 0.4))
      }
      if (shot.startsWith('ending-4')) chest.position.set(1.6, 1.5, -1.85)
      if (shot === 'ending-5b') chest.position.set(1.7, 0.12, -1.9)

    }
    if (hasLastBag(shot)) {
      const bag = show('StoryLastBag', 3.8, 0.1, -0.2)
      if (chapter(shot) === 4 && beat.scene !== 'bubblebridge') bag.visible = false
      if (shot === 'intro-3a-01') {
        bag.position.set(1.5 + p * 2, 0.1 + Math.sin(p * Math.PI) * 1.2, 1.2)
        bag.scale.setScalar(smooth((progress - 0.55) / 0.2))
      }
      if (shot === 'intro-3d') bag.position.set(3.5 + p * 3.5, 0.1 + p * 3.1, 1.2 - p * 5)
      if (shot === 't5-1b') {
        bag.position.copy(storyShipBagPosition(progress))
        bag.rotation.y = Math.sin(p * Math.PI) * Math.PI / 2
      }
      if (beat.scene === 'ship' && shot !== 't5-1b') bag.position.set(-0.3, 0.1, 1.1)
      if (shot === 't6-tilt-01') bag.position.set(-0.3 + p * 0.45, 0.1 - p * 0.25, 1.1)
      if (shot === 't6-tilt-02') bag.position.set(0.15, -0.15 - p * 3.5, 1.1)
      if (beat.scene === 'deepsea') bag.position.set(0, 0.16, -0.75)
      if (shot === 't6-2a-01') {
        // The clam sits behind the landing lane, with the recovered bag tucked
        // into its shell. Keeping both at the same rear depth leaves a clean
        // landing corridor for Bob and Zap instead of forcing the solver to
        // push the clam between them.
        bag.position.z = -1.6
        bag.position.y = 3.8 * (1 - smooth(progress / 0.5)) + 0.16
        bag.visible = progress < 0.66
      }
      // Inside a closed clam: keep it hidden until the authored recovery, rather
      // than rendering through the shell and pretending it has been recovered.
      if (shot === 't6-2b-01' || shot === 't6-2b-02') bag.visible = false
      if (shot === 't7-2a-02') bag.position.lerpVectors(new THREE.Vector3(0, 0.16, -0.75), bob.clone().add(new THREE.Vector3(0.6, 0.9, 0.45)), p)
      if (shot === 't7-2b-01') bag.position.copy(bob).add(new THREE.Vector3(-0.65, 0.08, 0.1))
      if (shot === 't7-3-01') bag.position.copy(storyCourierPosition(shot, progress, bob))
    }
    if (COURIER_SHOTS.includes(shot)) {
      const bag = show('StoryRecoveredBag', 0, 0, 0)
      bag.position.copy(storyCourierPosition(shot, progress, bob))
    }
    if (shot === 'intro-3d') {
      // The screenplay calls for one marked bag carried by Zap. The old
      // fallback spawned four unlabelled bags on the same trajectory, so they
      // crossed the cast and read as unexplained objects appearing in midair.
      // Coins are represented by the chest burst and the single carried bag.
    }
    if (hasChart(shot)) {
      const chart = attach('StoryCargoChart', bob, 0.57, 1.29, 0.17)
      chart.scale.setScalar(0.36)
      chart.rotation.z = -0.12
      if (shot.startsWith('t6-tilt')) {
        chart.position.copy(this.groups.get('StoryLastBag')!.position).add(new THREE.Vector3(0.18, 0.87, 0.15))
        chart.scale.setScalar(0.45)
      }
      if (shot === 't7-2a-02') {
        chart.scale.setScalar(0.85)
        chart.position.lerpVectors(new THREE.Vector3(0.18, 0.1, -0.5), bob.clone().add(new THREE.Vector3(0, 1.15, 0.55)), p)
      }
      if (shot === 't7-2b-01' || shot === 't8-1c') {
        chart.scale.setScalar(1)
        chart.position.copy(zap).add(new THREE.Vector3(-0.3, 0.65, 0.6))
        chart.rotation.x = -0.2
      }
      if (shot === 't8-2-02') {
        const gust = Math.sin(p * Math.PI)
        chart.scale.setScalar(0.36 + gust * 0.64)
        chart.position.add(new THREE.Vector3(-gust * 0.55, gust * 1.05, gust * 0.6))
        chart.rotation.z = -0.12 + gust * 0.4
      }
    }
    if (shot.startsWith('intro-') || shot.startsWith('ending-4') || shot === 'ending-5b') {
      const sign = show('StoryFeastSign', -3.35, 2.5, -2.75)
      if (shot === 'intro-4b') {
        sign.position.lerpVectors(new THREE.Vector3(bob.x, 0.1, bob.z + 0.45), bob.clone().add(new THREE.Vector3(0, 1.2, 0.5)), p)
        sign.rotation.x = -Math.PI / 2 * (1 - p)
      }
    }
    if (shot === 'intro-2c' || shot === 'intro-3a-01') {
      const tray = show('StoryServingTray', 0, 0, 0)
      if (shot === 'intro-2c') tray.position.copy(storyTrayPosition(shot,progress,bob))
      if (shot === 'intro-3a-01') {
        // The screenplay calls for one surviving plate in BlueBob's hands. The
        // serving tray stays out of this shot; moving it into the same lane made
        // a second, unlabelled white object cross the actors and read as a prop
        // appearing from nowhere.
        tray.visible = false
      }
      if (shot === 'intro-2c') {
        tray.rotation.z = Math.sin(p * Math.PI) * 0.17
      }
    }
    const introductoryStack = /^intro-2/.test(shot) || shot === 'intro-3a-01'
    if (introductoryStack) {
      const stack = show('StoryPlates',0,0,0)
      stack.position.copy(storyPlateStackPosition(shot,progress,bob))
      if (shot === 'intro-2a-01') stack.rotation.z = Math.sin(p * Math.PI * 2) * 0.035
      if (shot === 'intro-2b') stack.rotation.z = Math.sin(p * Math.PI * 2) * 0.14 * (1 - smooth(progress / 0.5))
    }
    if (['intro-2b','intro-2c','intro-3a-01'].includes(shot)) {
      show('StorySurvivingPlate',0,0,0).position.copy(storyLoosePlatePosition(shot,progress,bob))
    }
    if (shot === 'intro-4b') attach('StorySurvivingPlate', bob, 0.4, 0.2 * (1 - p), 0.6)
    if (shot === 'intro-4e-02') {
      const stack = show('StoryPlates', -0.55, 1.58, -1.8)
      stack.rotation.z = smooth((progress - 0.3) / 0.5) * 1.5
      stack.position.x += p * 0.4
      const plate = show('StorySurvivingPlate', -0.55, 2.14, -1.8)
      plate.position.lerpVectors(plate.position, krabs.clone().add(new THREE.Vector3(0.55, 1.35, 0.45)), smooth((progress - 0.55) / 0.35))
    }
    if (chapter(shot) >= 9 || (shot.startsWith('ending-') && beat.scene === 'throne')) {
      // Keep the empty banquet as the rear-cave landmark. The foreground is
      // the actor route; placing the table on that route made the walk-in and
      // satchel read as clipping through furniture.
      show('StoryEmptyBanquet', -4.6, 0, -3.2).rotation.y = 0.15
    }
    if (shot === 'ending-4c-02' || shot === 'ending-5b') {
      const chair = show('StoryHumbleChair', 2.1, 0.05, 0.4)
      if (shot === 'ending-4c-02') chair.position.lerpVectors(zap.clone().add(new THREE.Vector3(0, 0, 0.55)), chair.position, p)
      if (shot === 'ending-5b') show('StoryEmptyBanquet', 0, 0, -3.0).rotation.y = Math.PI / 2
    }
    if (shot === 't5-2-02' || shot === 't5-2-03') {
      show('StoryBarrelSupport',0,0,0)
      const barrel = show('StoryOccupiedBarrel',0,0,0)
      barrel.position.copy(storyBarrelPosition())
      this.barrelLid.rotation.x = shot === 't5-2-02' ? -0.7 + p * 0.6 : -0.1 - p * 0.5
      this.barrelLid.position.y = 1.13 + (shot === 't5-2-02' ? Math.sin(p*Math.PI)*0.07 : 0)
    }
    if (/^intro-(2|3a)/.test(shot)) show('StoryStaffStep',2.7,0,-2.65)
    if (FOOD_SHOTS.includes(shot)) {
      const food=show('StorySandwich',0,0,0)
      food.position.copy(storySandwichPosition(shot,progress,elapsed,positionOf,mouthOf))
      const eatStart=CUTSCENE_FOOD_CONTACT[shot] ?? (shot === 't2-2a-02' ? .24 : shot === 'ending-1b' ? .58 : .55)
      const eating=!['t2-1a-02','t2-1c-03'].includes(shot)
      if (eating) {
        const bite=smooth((progress-eatStart)/.3)
        food.scale.set(1-bite*.88,1-bite*.65,1-bite*.7)
        food.visible=progress<.94
      }
    }
    if (GIANT_FOOD_SHOTS.includes(shot) && beat.actions.some((action) => action.prop === 'giant-sandwich')) {
      const ring=show('StorySandwichRing',0,0,0)
      ring.position.copy(storyGiantRingCenter(bob))
      const center=show('StorySandwichCenter',0,0,0)
      center.position.copy(ring.position)
      const eaten=shot==='t7-3-03' ? 0 : shot==='t7-3-04' ? smooth((progress-.4)/.32) : 1
      center.scale.setScalar(1-eaten)
      center.visible=eaten<1
      if (shot==='t7-3-04') ring.position.y+=p*1.4
    }
    if (shot === 'ending-5b') show('StoryParkedThrone', -4.9, 0.05, 0.3).rotation.y = 0.25
    if (shot === 'ending-2a' || shot.startsWith('ending-2b')) {
      const crown = show('StoryRelinquishedCrown', king.x + 1.1, 0.05, king.z + 0.4)
      if (shot === 'ending-2a') {
        crown.visible = progress >= 0.25
        crown.position.lerpVectors(king.clone().add(new THREE.Vector3(0, 3.3, 0)), crown.position, smooth((progress - 0.25) / 0.75))
      }
    }
    this.updateAnimatedProp(progress, dt, positionOf)
    this.enforceSeabedContacts()
    if (matrixOf) this.updateAttachments(matrixOf)
  }

  /** Read-only motion audit for continuous capture and model review. Every
   * visible continuity prop reports its semantic purpose and world bounds. */
  storyPropSnapshot(): StoryPropSnapshot[] {
    const snapshot: StoryPropSnapshot[] = []
    for (const group of this.groups.values()) {
      group.updateMatrixWorld(true)
      const bounds = new THREE.Box3().setFromObject(group)
      snapshot.push({
        name: group.name,
        visible: group.visible,
        purpose: String(group.userData.storyPurpose ?? ''),
        position: { x: group.position.x, y: group.position.y, z: group.position.z },
        rotation: { x: group.rotation.x, y: group.rotation.y, z: group.rotation.z },
        bounds: { minY: bounds.min.y, maxY: bounds.max.y, width: bounds.max.x - bounds.min.x, depth: bounds.max.z - bounds.min.z },
      })
    }
    for (const [prop, holder] of this.animatedRoots) {
      holder.updateMatrixWorld(true)
      const bounds = new THREE.Box3().setFromObject(holder)
      snapshot.push({
        name: holder.name,
        visible: Boolean(this.animatedScene?.visible && holder.visible),
        purpose: `authored ${prop} continuity animation`,
        position: { x: holder.position.x, y: holder.position.y, z: holder.position.z },
        rotation: { x: holder.rotation.x, y: holder.rotation.y, z: holder.rotation.z },
        bounds: { minY: bounds.min.y, maxY: bounds.max.y, width: bounds.max.x - bounds.min.x, depth: bounds.max.z - bounds.min.z },
      })
    }
    return snapshot
  }

  collisionBodies(): CollisionBody[] {
    const bodies: CollisionBody[] = []
    for (const group of this.groups.values()) {
      if (!group.visible) continue
      const proxyByName: Record<string, { center: [number, number, number]; halfSize: [number, number, number] }> = {
        StoryFeastChest: { center: [0, 0.78, 0], halfSize: [0.92, 0.78, 0.58] },
        StoryPlates: { center: [0, 0.3, 0], halfSize: [0.46, 0.3, 0.44] },
        StoryPlatesSecond: { center: [0, 0.3, 0], halfSize: [0.46, 0.3, 0.44] },
        StorySurvivingPlate: { center: [0, 0.04, 0], halfSize: [0.38, 0.04, 0.38] },
        StoryServingTray: { center: [0, 0.1, 0], halfSize: [0.82, 0.1, 0.54] },
        StoryLastBag: { center: [0, 0.45, 0], halfSize: [0.48, 0.45, 0.34] },
        StoryRecoveredBag: { center: [0, 0.45, 0], halfSize: [0.48, 0.45, 0.34] },
        StorySandwich: { center: [0, 0.18, 0], halfSize: [0.42, 0.18, 0.34] },
        StorySandwichCenter: { center: [0, 0.18, 0], halfSize: [0.62, 0.18, 0.62] },
      }
      const proxy = proxyByName[group.name]
      if (proxy) group.userData.collisionProxy = proxy
      bodies.push({
        object: group,
        name: group.name,
        kind: 'story-prop',
        purpose: String(group.userData.storyPurpose ?? group.name),
      })
    }
    if (this.animatedScene?.visible) {
      for (const holder of this.animatedRoots.values()) {
        if (!holder.visible) continue
        bodies.push({
          object: holder,
          name: holder.name,
          kind: 'story-prop',
          purpose: String(holder.userData.storyPurpose ?? holder.name),
        })
      }
    }
    return bodies
  }

  /** Continuity props use the same seabed contact plane as the character roots.
   * Applying the correction after each shot's blocking keeps falling bags,
   * loose plates, and animated Blender props from tunnelling into the terrain. */
  private enforceSeabedContacts(): void {
    for (const group of this.groups.values()) {
      if (!group.visible) continue
      if (group.name === 'StoryLastBag' && this.beat?.scene === 'ship' && this.beat.shot.startsWith('t6-tilt')) continue
      group.updateMatrixWorld(true)
      const bounds = new THREE.Box3().setFromObject(group)
      if (!Number.isFinite(bounds.min.y) || bounds.min.y >= STORY_SEABED_Y) continue
      group.position.y += STORY_SEABED_Y - bounds.min.y
      group.updateMatrixWorld(true)
    }
    if (this.animatedScene?.visible) {
      for (const holder of this.animatedRoots.values()) {
        if (!holder.visible) continue
        holder.updateMatrixWorld(true)
        const bounds = new THREE.Box3().setFromObject(holder)
        if (!Number.isFinite(bounds.min.y) || bounds.min.y >= STORY_SEABED_Y) continue
        holder.position.y += STORY_SEABED_Y - bounds.min.y
      }
    }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.geometries.forEach((geometry) => geometry.dispose())
    this.materials.forEach((material) => material.dispose())
    this.textures.forEach((texture) => texture.dispose())
    this.root.removeFromParent()
    this.root.clear()
    this.animatedMixer?.stopAllAction()
    this.animatedMixer = null
    this.animatedScene = null
    this.animatedRoots.clear()
    this.animatedTargets.clear()
    this.animatedClips.clear()
  }

  private normaliseAnimatedRoot(root: THREE.Object3D, prop: DioramaProp): void {
    root.updateMatrixWorld(true)
    const box = new THREE.Box3().setFromObject(root)
    const size = box.getSize(new THREE.Vector3())
    const target = prop === 'bridge' ? 2.8 : prop === 'throne' ? 3.4 : prop === 'coin-chest' ? 1.8 : prop === 'coin-bag' ? 1.05 : prop === 'patty' ? 0.95 : 1.15
    const factor = target / Math.max(0.001, Math.max(size.x, size.y, size.z))
    root.scale.multiplyScalar(factor)
    root.updateMatrixWorld(true)
    const fitted = new THREE.Box3().setFromObject(root)
    const centre = fitted.getCenter(new THREE.Vector3())
    root.position.x -= centre.x
    root.position.y -= fitted.min.y
    root.position.z -= centre.z
    root.updateMatrixWorld(true)
    this.animatedBase.set(root, { position: root.position.clone(), rotation: root.rotation.clone(), scale: root.scale.clone() })
  }

  private updateAnimatedProp(progress: number, dt: number, positionOf: (character: Character) => THREE.Vector3): void {
    if (!this.animatedScene || !this.animatedMixer || !this.beat) return
    const authored = this.beat.actions.find((action) => {
      if (!action.prop || storyPropsOwnLegacy(this.beat!, action.prop)) return false
      const clipName = ANIMATED_PROP_CLIPS[action.prop]?.[action.type]
      return Boolean(clipName && this.animatedClips.has(clipName.toLowerCase()) && this.animatedRoots.has(action.prop))
    })
    if (!authored?.prop) {
      this.animatedScene.visible = false
      this.activeAnimatedProp = null
        this.animatedMixer.stopAllAction()
      return
    }
    const prop = authored.prop
    const root = this.animatedRoots.get(prop)
    const target = this.animatedTargets.get(prop)
    const clipName = ANIMATED_PROP_CLIPS[prop]?.[authored.type]
    const clip = clipName ? this.animatedClips.get(clipName.toLowerCase()) : undefined
    if (!root || !target || !clip) return
    // Other continuity props remain procedural and keep their authored handoffs.
    const legacyName = prop === 'coin-chest' ? 'StoryFeastChest'
      : prop === 'coin-bag' ? 'StoryLastBag'
        : prop === 'patty' ? 'StorySandwichCenter'
          : undefined
    if (legacyName) {
      const legacy = this.groups.get(legacyName)
      if (legacy?.visible) {
            this.activeAnimatedProp = null
        return
      }
      if (legacy) legacy.visible = false
    }
    this.animatedScene.visible = true
    this.animatedRoots.forEach((item) => { item.visible = item === root })
    const base = this.animatedBase.get(target)
    if (base) {
      target.position.copy(base.position)
      target.rotation.copy(base.rotation)
      target.scale.copy(base.scale)
    }
    // The holder is the shot-level transform. Reset it before applying the
    // current contact anchor; multiplying its previous scale caused exponential
    // shrinkage and left the authored prop crossing the next frame's target.
    root.position.set(0, 0, 0)
    root.rotation.set(0, 0, 0)
    root.scale.setScalar(1)
    const legacy = legacyName ? this.groups.get(legacyName) : undefined
    if (legacy) {
      root.position.copy(legacy.position)
      root.rotation.copy(legacy.rotation)
      root.scale.multiply(legacy.scale)
    } else if (prop === 'bridge') {
      root.position.set(0, 0.04, -0.1)
    } else if (prop === 'throne') {
      root.position.set(0, 0, -3.1)
    } else {
      root.position.copy(positionOf(authored.actor)).add(new THREE.Vector3(.6, 1.2, .5))
    }
    this.animatedMixer.stopAllAction()
    const action = this.animatedMixer.clipAction(clip)
    action.reset().setLoop(THREE.LoopOnce, 1)
    action.clampWhenFinished = true
    action.play()
    this.animatedMixer.setTime(clamp(progress) * clip.duration)
    this.activeAnimatedProp = prop
    // Keep the prop's authored motion readable within the shot without letting a
    // long-running clip accumulate transform drift.
    root.rotation.y += Math.sin(progress * Math.PI) * 0.015
  }

  private foodAndSupports(): void {
    const food=this.group('StorySandwich')
    for (const [role,y,h] of [['bread',-.12,.17],['jelly',0,.09],['cream',.12,.17]] as const) this.box(food,role,0,y,0,.72,h,.55)
    this.bake(food)
    const ring=this.group('StorySandwichRing'), center=this.group('StorySandwichCenter')
    for (const [role,y,h] of [['bread',-.12,.17],['jelly',0,.09],['cream',.12,.17]] as const) {
      const shape=new THREE.Shape()
      shape.absarc(0,0,1.22,0,Math.PI*2,false)
      const hole=new THREE.Path(); hole.absarc(0,0,.77,0,Math.PI*2,true);shape.holes.push(hole)
      const geo=new THREE.ExtrudeGeometry(shape,{depth:h,bevelEnabled:true,bevelThickness:.025,bevelSize:.025,bevelSegments:2,curveSegments:24})
      geo.rotateX(-Math.PI/2)
      this.part(ring,geo,role,0,y-h/2,0)
      this.part(center,new THREE.CylinderGeometry(.78,.78,h,32),role,0,y,0)
    }
    this.bake(ring);this.bake(center)
    const step=this.group('StoryStaffStep')
    this.box(step,'wood',0,.31,0,1.35,.62,1.05)
    this.box(step,'wood',0,.625,0,1.42,.05,1.12)
    this.bake(step)
    const support=this.group('StoryBarrelSupport')
    for(let i=0;i<4;i++)this.box(support,'wood',.2,-.08,.45+i*.28,5.8,.16,.26)
    this.bake(support)
  }

  private material(role: string): THREE.MeshStandardMaterial {
    const existing = this.materials.get(role)
    if (existing) return existing
    const colors: Record<string, number> = { wood:0x715239, leather:0x493d31, navy:0x293f4b, brass:0x9b804d,
      red:0x943c30, cream:0xd8cfad, bread:0xbd965f, jelly:0x69463d, ceramic:0xd4d5c9, ink:0x243b46 }
    const material = new THREE.MeshStandardMaterial({ color:colors[role] ?? 0xffffff,
      roughness:role === 'ceramic' ? 0.24 : role === 'brass' ? 0.36 : 0.79,
      metalness:role === 'brass' ? 0.65 : 0 })
    material.name = `Story_${role}`
    this.materials.set(role, material)
    return material
  }

  private group(name: string): THREE.Group {
    const group = new THREE.Group()
    group.name = name
    const purposes: Record<string, string> = {
      StoryServingTray: 'serving tray used during the counter handoff',
      StorySurvivingPlate: "BlueBob's one surviving plate in the opening accident",
      StoryPlates: 'remaining plate stack for the Shack counter',
      StoryPlatesSecond: 'second returned plate stack at the feast',
      StoryFeastChest: "Money Krabs' marked coin chest",
      StoryLastBag: 'stolen red-tied coin bag',
      StorySpilledBag0: 'spilled coin bag from the opened chest',
      StorySpilledBag1: 'spilled coin bag from the opened chest',
      StorySpilledBag2: 'spilled coin bag from the opened chest',
      StorySpilledBag3: 'spilled coin bag from the opened chest',
    }
    group.userData.storyPurpose = purposes[name] ?? name.replace(/^Story/, 'story ')
    this.root.add(group)
    this.groups.set(name, group)
    return group
  }

  private part(group: THREE.Object3D, geometry: THREE.BufferGeometry, role: string, x=0,y=0,z=0,rx=0,ry=0,rz=0): THREE.Mesh {
    const mesh = new THREE.Mesh(geometry, this.material(role))
    mesh.position.set(x,y,z)
    mesh.rotation.set(rx,ry,rz)
    mesh.castShadow = mesh.receiveShadow = true
    group.add(mesh)
    return mesh
  }

  private box(group: THREE.Object3D, role: string, x:number,y:number,z:number,w:number,h:number,d:number): THREE.Mesh {
    return this.part(group,new RoundedBoxGeometry(w,h,d,2,Math.min(w,h,d)*0.15),role,x,y,z)
  }

  private tube(group: THREE.Object3D, role: string, points: number[][], radius:number): void {
    const curve = new THREE.CatmullRomCurve3(points.map(([x,y,z]) => new THREE.Vector3(x,y,z)))
    this.part(group,new THREE.TubeGeometry(curve,20,radius,6,false),role)
  }

  private plate(group: THREE.Object3D,x:number,y:number,z:number,r=0.34): void {
    this.part(group,new THREE.CylinderGeometry(r,r*0.87,0.035,24),'ceramic',x,y,z)
    this.part(group,new THREE.TorusGeometry(r*0.87,0.016,5,24),'ceramic',x,y+0.025,z,Math.PI/2)
  }

  private bake(group: THREE.Group): void {
    const batches = new Map<THREE.Material, THREE.BufferGeometry[]>()
    const meshes = group.children.filter((part): part is THREE.Mesh => part instanceof THREE.Mesh)
    for(const mesh of meshes) {
      mesh.updateMatrix()
      const geometry = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone()
      geometry.applyMatrix4(mesh.matrix)
      mesh.geometry.dispose()
      const material = mesh.material as THREE.Material
      const pieces = batches.get(material) ?? []
      pieces.push(geometry)
      batches.set(material,pieces)
      mesh.removeFromParent()
    }
    for(const [material,pieces] of batches) {
      const geometry = mergeGeometries(pieces,false)
      pieces.forEach(piece => piece.dispose())
      if(!geometry) throw new Error('Story prop geometry batch failed')
      this.geometries.add(geometry)
      const mesh = new THREE.Mesh(geometry,material)
      mesh.castShadow = mesh.receiveShadow = true
      group.add(mesh)
    }
  }

  private satchel(): void {
    const g=this.group('StorySatchel')
    this.box(g,'navy',0.62,0.92,0.18,0.56,0.48,0.32)
    this.box(g,'leather',0.62,1.15,0.22,0.58,0.12,0.37)
    this.tube(g,'leather',[[-0.32,1.91,0.18],[0.03,1.61,0.31],[0.62,1.06,0.36]],0.036)
    this.tube(g,'leather',[[-0.32,1.91,-0.12],[0.1,1.55,-0.2],[0.62,0.95,0.03]],0.03)
    this.box(g,'brass',0.62,0.95,0.365,0.12,0.14,0.04)
    for(const x of [0.44,0.54]) this.box(g,'bread',x,1.29,0.2,0.15,0.28,0.17)
    this.part(g,new THREE.CylinderGeometry(0.1,0.11,0.29,16),'jelly',0.78,1.28,0.16)
    this.part(g,new THREE.CylinderGeometry(0.12,0.12,0.05,16),'brass',0.78,1.445,0.16)
    this.bake(g)
  }

  private chest(): void {
    const g=this.group('StoryFeastChest')
    this.box(g,'wood',0,0.43,0,1.48,0.82,0.95)
    for(const x of [-0.55,0.55]) this.box(g,'brass',x,0.44,0.012,0.09,0.84,1.01)
    for(const side of [-1,1]) this.part(g,new THREE.TorusGeometry(0.17,0.035,6,16),'brass',side*0.83,0.52,0,0,Math.PI/2)
    // Unique shell crest stays on the original chest throughout its entire route.
    this.part(g,new THREE.CylinderGeometry(0.23,0.23,0.045,24),'brass',0,0.53,0.51,Math.PI/2)
    for(let i=0;i<5;i++) {
      const a=(i-2)*0.27
      this.tube(g,'navy',[[0,0.38,0.54],[Math.sin(a)*0.17,0.63,0.54]],0.013)
    }
    for(let i=0;i<21;i++) this.part(g,new THREE.CylinderGeometry(0.09,0.09,0.035,12),'brass',(i%7-3)*0.16,0.85+Math.floor(i/7)*0.03,(i%3-1)*0.2)
    this.bake(g)
    this.chestLid.name='StoryChestLid'
    this.chestLid.position.set(0,0.83,-0.43)
    g.add(this.chestLid)
    this.box(this.chestLid,'wood',0,0.17,0.43,1.53,0.36,1)
    for(const x of [-0.55,0.55]) this.box(this.chestLid,'brass',x,0.2,0.43,0.1,0.38,1.03)
    this.bake(this.chestLid)
  }

  private lastBag(): void {
    const g=this.group('StoryLastBag')
    const sack=this.part(g,new THREE.SphereGeometry(0.45,20,14),'cream',0,0.47,0)
    sack.scale.set(0.95,1.08,0.8)
    this.part(g,new THREE.CylinderGeometry(0.19,0.25,0.2,14),'cream',0,0.92,0)
    this.part(g,new THREE.TorusGeometry(0.22,0.04,6,20),'red',0,0.89,0,Math.PI/2)
    this.tube(g,'red',[[0.16,0.9,0.1],[0.36,1.02,0.17],[0.39,0.86,0.2],[0.15,0.87,0.12]],0.03)
    this.tube(g,'red',[[0.16,0.9,0.1],[0.38,0.62,0.23],[0.3,0.48,0.25]],0.038)
    this.tube(g,'leather',[[-0.15,0.92,0],[0,1.2,0],[0.15,0.92,0]],0.025)
    this.bake(g)
  }

  private plainBag(name: string): void {
    const g = this.group(name)
    const sack = this.part(g, new THREE.SphereGeometry(0.27, 16, 10), 'cream', 0, 0.28, 0)
    sack.scale.set(1, 1.1, 0.8)
    this.part(g, new THREE.TorusGeometry(0.13, 0.027, 5, 16), 'leather', 0, 0.54, 0, Math.PI / 2)
    this.tube(g, 'leather', [[-0.1,0.55,0],[0,0.73,0],[0.1,0.55,0]],0.022)
    this.bake(g)
  }

  private chart(): void {
    const g=this.group('StoryCargoChart')
    const shape=new THREE.Shape()
    shape.moveTo(-0.65,-0.44);shape.lineTo(0.65,-0.44);shape.lineTo(0.65,0.17)
    shape.quadraticCurveTo(0.43,0.14,0.49,0.3);shape.quadraticCurveTo(0.3,0.26,0.37,0.44)
    shape.lineTo(-0.65,0.44);shape.closePath()
    this.part(g,new THREE.ExtrudeGeometry(shape,{depth:0.035,bevelEnabled:true,bevelSize:0.012,bevelThickness:0.008,bevelSegments:1}),'cream')
    this.tube(g,'red',[[-0.48,-0.27,0.055],[-0.2,-0.12,0.055],[-0.3,0.17,0.055],[0.03,0.26,0.055],[0.34,0.12,0.055]],0.015)
    this.box(g,'wood',0.33,0.08,0.075,0.29,0.2,0.06)
    for(const x of [0.23,0.43]) this.box(g,'brass',x,0.08,0.11,0.025,0.2,0.03)
    for(let i=0;i<3;i++) this.part(g,new THREE.ConeGeometry(0.038,0.12,5),'brass',0.24+i*0.09,0.27,0.065)
    this.box(g,'ink',-0.45,0.24,0.055,0.02,0.19,0.02)
    this.box(g,'ink',-0.45,0.24,0.055,0.15,0.02,0.02)
    this.bake(g)
  }

  private plateStack(name:string,count:number): void {
    const g=this.group(name)
    for(let i=0;i<count;i++) this.plate(g,Math.sin(i)*0.012,i*0.069,0)
    this.bake(g)
  }

  private feastSign(): void {
    const g=this.group('StoryFeastSign')
    this.box(g,'wood',0,0,0,2.25,1.02,0.11)
    this.box(g,'brass',0,-0.46,0.065,2.1,0.035,0.025)
    if(typeof document !== 'undefined') {
      const canvas=document.createElement('canvas');canvas.width=768;canvas.height=320
      const ctx=canvas.getContext('2d')
      if(ctx) {
        ctx.fillStyle='#d8cfad';ctx.fillRect(0,0,768,320)
        ctx.strokeStyle='#715239';ctx.lineWidth=10;ctx.strokeRect(16,16,736,288)
        ctx.textAlign='center';ctx.fillStyle='#243b46';ctx.font='bold 88px Georgia, serif'
        ctx.fillText('TOWN FEAST',384,132)
        ctx.font='bold 62px Georgia, serif';ctx.fillText('TOMORROW',384,237)
        const texture=new THREE.CanvasTexture(canvas);texture.colorSpace=THREE.SRGBColorSpace
        texture.anisotropy=4;this.textures.add(texture)
        const material=new THREE.MeshStandardMaterial({map:texture,roughness:0.86})
        this.materials.set('feast-lettering',material)
        const geometry=new THREE.PlaneGeometry(2.1,0.89)
        const face=new THREE.Mesh(geometry,material);face.position.z=0.066;face.receiveShadow=true;g.add(face)
      }
    }
    // Lettering remains a material on a thick, shadow-casting wooden board.
    g.userData.label='TOWN FEAST — TOMORROW'
    this.bake(g)
  }

  private servingTray(): void {
    const g = this.group('StoryServingTray')
    this.box(g,'navy',0,0,0,1.72,0.065,1.12)
    for (const x of [-0.83,0.83]) this.box(g,'brass',x,0.045,0,0.05,0.08,1.12)
    for (const z of [-0.53,0.53]) this.box(g,'brass',0,0.045,z,1.68,0.08,0.05)
    for (const x of [-0.95,0.95]) this.part(g,new THREE.TorusGeometry(0.13,0.023,5,16),'brass',x,0.03,0,Math.PI/2)
    this.bake(g)
  }

  private banquet(): void {
    const g=this.group('StoryEmptyBanquet')
    this.box(g,'wood',0,0.95,0,1.5,0.16,4.2)
    for(const x of [-0.56,0.56]) for(const z of [-1.72,1.72]) this.box(g,'wood',x,0.46,z,0.12,0.9,0.12)
    this.box(g,'navy',0,1.05,0,0.46,0.025,4.05)
    for(const z of [-1.35,0,1.35]) for(const x of [-0.48,0.48]) {
      this.plate(g,x,1.065,z,0.21)
      this.part(g,new THREE.CylinderGeometry(0.065,0.055,0.16,12),'brass',x,1.16,z+0.32)
      this.box(g,'brass',x+(x<0?-0.28:0.28),1.08,z,0.025,0.025,0.3)
      this.box(g,'wood',x*2.55,0.47,z,0.52,0.1,0.48)
      this.box(g,'wood',x*2.55,0.84,z-0.21,0.52,0.65,0.08)
      for(const dx of [-0.19,0.19]) for(const dz of [-0.17,0.17]) this.box(g,'wood',x*2.55+dx,0.23,z+dz,0.065,0.45,0.065)
    }
    this.bake(g)
  }

  private chair(): void {
    const g=this.group('StoryHumbleChair')
    this.box(g,'wood',0,0.48,0,0.78,0.12,0.65)
    for(const x of [-0.29,0.29]) for(const z of [-0.23,0.23]) this.box(g,'wood',x,0.23,z,0.08,0.46,0.08)
    for(const x of [-0.29,0.29]) this.box(g,'wood',x,0.91,-0.24,0.08,0.8,0.08)
    for(const y of [0.92,1.17]) this.box(g,'wood',0,y,-0.24,0.66,0.1,0.08)
    this.box(g,'navy',0,0.56,0,0.68,0.065,0.56)
    this.bake(g)
  }

  private occupiedBarrel(): void {
    const g=this.group('StoryOccupiedBarrel')
    // Separate staves and an open top leave the hiding guard physically visible.
    for(let i=0;i<16;i++) {
      const a=i/16*Math.PI*2
      const stave=this.box(g,'wood',Math.sin(a)*0.45,0.55,Math.cos(a)*0.45,0.175,1.1,0.08)
      stave.rotation.y=a
    }
    for(const y of [0.15,0.93]) this.part(g,new THREE.TorusGeometry(0.47,0.04,6,24),'navy',0,y,0,Math.PI/2)
    this.bake(g)
    this.barrelLid.name='StoryBarrelLid'
    this.barrelLid.position.set(0,1.13,-0.4)
    g.add(this.barrelLid)
    this.part(this.barrelLid,new THREE.CylinderGeometry(0.49,0.49,0.06,20),'wood',0,0,0.4)
    this.box(this.barrelLid,'navy',0,0.043,0.4,0.72,0.025,0.05)
    this.bake(this.barrelLid)
  }

  private parkedThrone(): void {
    const g=this.group('StoryParkedThrone')
    this.box(g,'navy',0,0.53,0,1.65,0.5,1.25)
    this.box(g,'navy',0,1.75,-0.48,1.85,2.3,0.26)
    for(const x of [-0.83,0.83]) {
      this.box(g,'brass',x,1.46,-0.42,0.13,2.7,0.17)
      this.box(g,'wood',x,0.2,0.24,0.12,0.4,0.16)
      this.box(g,'brass',x,1.03,0.1,0.17,0.14,1.2)
    }
    for(let i=0;i<5;i++) this.part(g,new THREE.ConeGeometry(0.12,0.35,6),'brass',-0.7+i*0.35,3,-0.42)
    // An ordinary shell cart supplies scale and makes the abandoned royal
    // furniture read as awkward street parking rather than a second throne room.
    this.box(g,'wood',1.58,0.35,0.2,0.9,0.12,0.7)
    for(const x of [1.15,2.02]) this.part(g,new THREE.TorusGeometry(0.21,0.045,6,18),'navy',x,0.23,0.2,0,Math.PI/2)
    this.tube(g,'wood',[[1.12,0.4,0.5],[1.15,0.85,0.8],[2,0.85,0.8],[2.02,0.4,0.5]],0.025)
    this.part(g,new THREE.SphereGeometry(0.37,16,8,0,Math.PI*2,0,Math.PI/2),'cream',1.58,0.42,0.2)
    this.bake(g)
  }

  private crown(): void {
    const g=this.group('StoryRelinquishedCrown')
    this.part(g,new THREE.CylinderGeometry(0.56,0.53,0.15,20,1,true),'brass',0,0.12,0)
    for(let i=0;i<7;i++) {
      const a=i/7*Math.PI*2
      this.part(g,new THREE.ConeGeometry(0.1,0.32,6),'brass',Math.sin(a)*0.54,0.34,Math.cos(a)*0.54)
    }
    this.bake(g)
  }
}
